import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/ui/input-group";
import { Kbd } from "@/ui/kbd";
import { ICONES, Icone, s } from "../visual.jsx";
import { useAcoes, useEstado, useOcupado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { acharNaConversa } from "../conversa.js";

/**
 * Procurar DENTRO da conversa aberta.
 *
 * A busca da mesa acha o atendimento; esta acha a mensagem. É a pergunta de
 * quem precisa do número do processo que o cliente mandou em abril, e a única
 * alternativa era rolar oito meses de conversa com o dedo.
 *
 * Mora na URL (`?naConversa=`) como todo recorte da mesa: o link se manda por
 * mensagem, o voltar do navegador fecha a busca e recarregar cai no mesmo termo.
 *
 * O que ela NÃO faz é mentir sobre o alcance: só o que já está carregado é
 * procurado, e quando existe histórico anterior a barra oferece trazê-lo em vez
 * de dizer "nenhum resultado" sobre um pedaço da conversa que nunca foi lido.
 */
export default function BuscaNaConversa({ mensagens }) {
  const { antigasFim, apontada } = useEstado();
  const ocupado = useOcupado();
  const { apontarMensagem, carregarAntigas } = useAcoes();
  const { naConversa, ajustar } = useNavegacao();
  const campo = useRef(null);

  /* O que se DIGITA mora aqui; o que se PROCURA mora na URL, 250ms depois da
     última tecla. Gravar a URL a cada letra redesenhava a mesa inteira (todo
     leitor de `useNavegacao`) e todas as bolhas com o termo novo — numa
     conversa longa a digitação travava. */
  const [texto, definirTexto] = useState(naConversa);
  useEffect(() => {
    if (texto === naConversa) return;
    const t = setTimeout(() => ajustar({ naConversa: texto }), 250);
    return () => clearTimeout(t);
  }, [texto, naConversa, ajustar]);

  const achados = useMemo(() => acharNaConversa(mensagens, naConversa), [mensagens, naConversa]);
  const onde = achados.indexOf(apontada);

  /* Abrir a busca põe o cursor nela: a barra que aparece e ainda pede um clique
     no campo custa dois gestos para uma tecla de atalho. */
  useEffect(() => {
    campo.current?.focus();
  }, []);

  /* O primeiro achado é apontado sozinho — quem digita "processo" quer ver a
     última vez que aquilo foi dito, não uma contagem. */
  const jaApontou = useRef("");
  useEffect(() => {
    if (jaApontou.current === naConversa) return;
    jaApontou.current = naConversa;
    if (achados.length) apontarMensagem(achados[0]);
  }, [achados, apontarMensagem, naConversa]);

  const andar = (passo) => {
    if (!achados.length) return;
    /* Nada apontado ainda (a pessoa rolou a conversa e o realce já apagou):
       qualquer seta volta para o primeiro achado, que é o mais recente. */
    if (onde < 0) return apontarMensagem(achados[0]);
    apontarMensagem(achados[(onde + passo + achados.length) % achados.length]);
  };

  const fechar = () => ajustar({ naConversa: null, procurar: null });

  return (
    <div
      className="flex flex-none items-center gap-2 border-b border-b-foreground/9 bg-secondary px-3.5 py-2"
      style={s("animation:vFade var(--t-curta) var(--e-entra) both")}
    >
      {/* `InputGroup` é quem devolve o anel de foco que a regra global do
          `index.html` tira do `input` — e o ícone, o contador e o atalho ficam
          DENTRO da moldura, com o alvo de clique inteiro caindo no campo. */}
      <InputGroup className="h-auto min-w-0 flex-1 rounded-[10px] border border-foreground/13 bg-card px-2.5">
        <InputGroupAddon align="inline-start" className="pl-0">
          <Icone de={ICONES.busca} tamanho={15} largura={1.9} cor="var(--color-tinta-fraca)" estilo={s("flex:none")} />
        </InputGroupAddon>
        <InputGroupInput
          ref={campo}
          value={texto}
          onChange={(e) => definirTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") fechar();
            if (e.key !== "Enter") return;
            /* Enter antes da espera procura já: andar em achados do termo velho
               seria pular para a mensagem errada. */
            if (texto !== naConversa) ajustar({ naConversa: texto });
            else andar(e.shiftKey ? -1 : 1);
          }}
          placeholder="Procurar nesta conversa…"
          aria-label="Procurar nesta conversa"
          className="h-auto min-w-0 flex-1 py-[5px] text-corpo text-foreground"
        />
        {naConversa.trim() ? (
          <InputGroupAddon align="inline-end" className="gap-1.5 pr-0">
            <span className="font-mono text-selo text-muted-foreground">
              {achados.length ? `${onde < 0 ? 1 : onde + 1}/${achados.length}` : "0"}
            </span>
            {/* O atalho dito com todas as letras: Enter anda nos achados, e
                antes disso só a dica das setas contava isso. */}
            {achados.length > 1 ? <Kbd>⏎</Kbd> : null}
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      {achados.length > 1 && (
        <>
          <BotaoDaBarra rotulo="Achado anterior (Shift+Enter)" icone={ICONES.baixo} girar aoTocar={() => andar(-1)} />
          <BotaoDaBarra rotulo="Próximo achado (Enter)" icone={ICONES.baixo} aoTocar={() => andar(1)} />
        </>
      )}

      {/* A honestidade do alcance: sem isto, "0" sobre uma conversa cujo começo
          nunca foi carregado é uma resposta errada com cara de certa. */}
      {!antigasFim && naConversa.trim() ? (
        <Button
          variant="outline"
          size="xs"
          onClick={carregarAntigas}
          className="h-auto flex-none rounded-lg border-verde-fundo/30 bg-verde-fundo/9 px-[9px] py-[5px] text-selo font-bold text-verde-fundo"
        >
          {ocupado === "antigas" ? "Trazendo…" : "Procurar mais atrás"}
        </Button>
      ) : null}

      <BotaoDaBarra rotulo="Fechar a busca" icone={ICONES.fechar} aoTocar={fechar} />
    </div>
  );
}

/** Um botão de ícone da barra — o mesmo alvo dos outros da mesa. */
function BotaoDaBarra({ rotulo, icone, aoTocar, girar = false }) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={aoTocar}
      data-dica={rotulo}
      aria-label={rotulo}
      className={`size-[30px] flex-none rounded-[9px] text-tinta-suave ${girar ? "rotate-180" : ""}`}
    >
      <Icone de={icone} tamanho={15} largura={2} />
    </Button>
  );
}
